import { useRef } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { DistrictDeckMetrics, SchoolSeries } from './districtReportDeck';
import { useSlideDeckPdf } from '../SchoolReport/useSlideDeckPdf';
import {
  DayOfWeekSlide,
  ElementaryMoodSlide,
  GamesSlide,
  OverviewMetrics,
  SecondaryMoodSlide,
  StoriesSlide,
  fitTitleFontSize,
} from '../SchoolReport/SchoolReportPDFSlides';
import styles from '../SchoolReport/SchoolReportPDFSlides.module.scss';
import districtStyles from './DistrictReportPDFSlides.module.scss';

// Same palettes the check-in deck script uses for its per-school line charts (mixpanelDataToSlides.ts)
// - educators and students get different palettes so the two slides are easy to tell apart.
const EDUCATOR_SCHOOL_COLORS = [
  '#e63946',
  '#1d3557',
  '#2a9d8f',
  '#e9c46a',
  '#264653',
  '#d62828',
  '#457b9d',
  '#a8dadc',
  '#f4a261',
  '#e76f51',
  '#606c38',
  '#283618',
  '#9d4edd',
  '#c77dff',
  '#5a189a',
];
const STUDENT_SCHOOL_COLORS = [
  '#f72585',
  '#7209b7',
  '#3a0ca0',
  '#4361ee',
  '#4cc9f0',
  '#ff6b6b',
  '#4ecdc4',
  '#45b7d1',
  '#96ceb4',
  '#ffeaa7',
  '#d63031',
  '#0984e3',
  '#00b894',
  '#e17055',
  '#6c5ce7',
];

// The deck script fits every school on one chart; past ~12 lines (and legend rows) a fixed-size
// slide stops being readable, so larger districts continue onto additional slides instead.
const SCHOOLS_PER_CHART_SLIDE = 12;
// Three side-by-side tables per slide, same as the deck template's top-educators slide.
const SCHOOLS_PER_EDUCATOR_SLIDE = 3;

const chunk = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};

const pageSuffix = (index: number, count: number) => (count > 1 ? ` (${index + 1} of ${count})` : '');

// Month labels come in as "Aug-2025". On one line they only fit about 8 months across the chart, so
// past that put the year on a second line - otherwise the labels run into each other.
const SINGLE_LINE_MONTH_TICKS = 8;
// Even stacked, about 14 labels fit across the chart; past that only label every Nth month.
const MAX_VISIBLE_MONTH_TICKS = 14;
const monthTickInterval = (count: number) => Math.max(0, Math.ceil(count / MAX_VISIBLE_MONTH_TICKS) - 1);

const MonthTick = ({
  x,
  y,
  payload,
  count,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  count: number;
}) => {
  const label = payload?.value ?? '';
  const [month, year] = label.split('-');
  const fontSize = 13;
  if (count <= SINGLE_LINE_MONTH_TICKS || !year) {
    return (
      <text x={x} y={y} dy={14} textAnchor='middle' fontSize={fontSize} fill='#666'>
        {label}
      </text>
    );
  }
  return (
    <text x={x} y={y} textAnchor='middle' fontSize={fontSize} fill='#666'>
      <tspan x={x} dy={14}>
        {month}
      </tspan>
      <tspan x={x} dy={fontSize + 2}>
        {year}
      </tspan>
    </text>
  );
};

const MultiSchoolLineChart = ({
  monthLabels,
  series,
  colors,
}: {
  monthLabels: string[];
  series: SchoolSeries[];
  colors: string[];
}) => {
  const data = monthLabels.map((month, i) =>
    Object.fromEntries([['month', month], ...series.map((school, s) => [`s${s}`, school.data[i]])]),
  );
  return (
    <div className={districtStyles.lineChartArea}>
      {/* Fixed-size (not ResponsiveContainer) and unanimated, so the chart is fully drawn the moment
          it mounts - html2canvas captures the slide shortly after, with no resize/animation frame. */}
      <LineChart width={586} height={467} data={data} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
        <CartesianGrid vertical={false} stroke='#e5e5e5' />
        <XAxis
          dataKey='month'
          tick={<MonthTick count={monthLabels.length} />}
          height={monthLabels.length > SINGLE_LINE_MONTH_TICKS ? 46 : 30}
          interval={monthTickInterval(monthLabels.length)}
          padding={{ left: 24, right: 24 }}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 13 }}
          label={{ value: 'Logins', angle: -90, position: 'insideLeft', fontSize: 14 }}
        />
        {series.map((school, s) => (
          <Line
            key={school.name}
            dataKey={`s${s}`}
            stroke={colors[s % colors.length]}
            fill={colors[s % colors.length]}
            strokeWidth={3}
            dot={{ r: 5 }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
      <div className={districtStyles.lineChartLegend}>
        {series.map((school, s) => (
          <div className={districtStyles.lineChartLegendItem} key={school.name}>
            <span
              className={districtStyles.lineChartLegendSwatch}
              style={{ backgroundColor: colors[s % colors.length] }}
            />
            {/* Only the name is truncated - the total is the point of the legend, so it always shows. */}
            <span className={districtStyles.lineChartLegendText}>{school.name}</span>
            <span className={districtStyles.lineChartLegendTotal}>{school.total}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EngagementSlides = ({
  title,
  dateRangeLabel,
  monthLabels,
  series,
  total,
  colors,
}: {
  title: string;
  dateRangeLabel: string;
  monthLabels: string[];
  series: SchoolSeries[];
  total: number;
  colors: string[];
}) => {
  // Always at least one slide, even with no logins, so the deck's slide order stays the same.
  const pages = series.length ? chunk(series, SCHOOLS_PER_CHART_SLIDE) : [[]];
  return (
    <>
      {pages.map((page, index) => (
        <div className={styles.slide} data-deck-slide key={index}>
          <div className={styles.blueSlideTitle}>
            {title}
            {pageSuffix(index, pages.length)}
          </div>
          <div className={styles.overviewPeriod}>{dateRangeLabel}</div>
          <MultiSchoolLineChart
            monthLabels={monthLabels}
            series={page}
            // Keep each school's color stable across continuation slides.
            colors={page.map((_, s) => colors[(index * SCHOOLS_PER_CHART_SLIDE + s) % colors.length])}
          />
          <div className={styles.totalBox}>
            <div className={styles.totalBoxLabel}>Total:</div>
            <div className={styles.totalBoxValue}>{total}</div>
          </div>
        </div>
      ))}
    </>
  );
};

interface DeckProps {
  metrics: DistrictDeckMetrics;
  fileName: string;
  onComplete: () => void;
}

const DistrictReportPDFSlides = ({ metrics, fileName, onComplete }: DeckProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useSlideDeckPdf(containerRef, fileName, onComplete, 'district report');

  const {
    districtName,
    dateRangeLabel,
    overview,
    monthLabels,
    educatorLoginsTotal,
    studentLoginsTotal,
    educatorLoginsBySchool,
    studentLoginsBySchool,
    educatorDayOfWeek,
    mostUsedDay,
    topEducatorsBySchool,
    elementaryMoods,
    elementaryUpMoodPercent,
    elementaryStudents,
    elementaryMoodsByMonth,
    secondaryMoods,
    secondaryUpMoodPercent,
    secondaryStudents,
    secondaryMoodsByMonth,
    topGames,
    topStories,
  } = metrics;

  const educatorPages = chunk(topEducatorsBySchool, SCHOOLS_PER_EDUCATOR_SLIDE);

  return (
    <div ref={containerRef} className={styles.pdfContainer}>
      <div className={styles.slide} data-deck-slide>
        <div className={styles.coverBand} />
        <div className={styles.coverContent}>
          <h1 className={styles.coverTitle} style={{ fontSize: fitTitleFontSize(districtName, 107) }}>
            {districtName}
          </h1>
        </div>
        <div className={styles.coverDate}>{dateRangeLabel}</div>
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.largeSlideTitle} style={{ fontSize: 52 }}>
          {`${districtName} - Overview`}
        </div>
        <div className={styles.overviewPeriod}>{dateRangeLabel}</div>
        <OverviewMetrics overview={overview} />
      </div>

      <EngagementSlides
        title='Educator Engagement - By School'
        dateRangeLabel={dateRangeLabel}
        monthLabels={monthLabels}
        series={educatorLoginsBySchool}
        total={educatorLoginsTotal}
        colors={EDUCATOR_SCHOOL_COLORS}
      />
      <EngagementSlides
        title='Student Engagement - By School'
        dateRangeLabel={dateRangeLabel}
        monthLabels={monthLabels}
        series={studentLoginsBySchool}
        total={studentLoginsTotal}
        colors={STUDENT_SCHOOL_COLORS}
      />

      {educatorPages.map((page, index) => (
        <div className={styles.slide} data-deck-slide key={index}>
          <div className={styles.powerUsersTitle}>
            Peekapak Power Users:
            <br />
            Our Most Engaged Educators!
          </div>
          <div className={styles.slideSubtitle} style={{ top: 146 }}>
            Below are educators with the most logins at each school.
          </div>
          <div className={districtStyles.educatorTables}>
            {page.map((school) => (
              <table className={districtStyles.educatorTable} key={school.schoolName}>
                <thead>
                  <tr>
                    <th colSpan={2}>{school.schoolName}</th>
                  </tr>
                </thead>
                <tbody>
                  {school.educators.map((educator) => (
                    <tr key={educator.name}>
                      <td>{educator.name}</td>
                      <td className={districtStyles.educatorCount}>{educator.logins}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
          {educatorPages.length > 1 && (
            <div className={districtStyles.pageIndicator}>
              {index + 1} of {educatorPages.length}
            </div>
          )}
        </div>
      ))}

      <ElementaryMoodSlide
        dateRangeLabel={dateRangeLabel}
        moodsByMonth={elementaryMoodsByMonth}
        moods={elementaryMoods}
        upMoodPercent={elementaryUpMoodPercent}
        students={elementaryStudents}
      />
      <SecondaryMoodSlide
        dateRangeLabel={dateRangeLabel}
        moodsByMonth={secondaryMoodsByMonth}
        moods={secondaryMoods}
        upMoodPercent={secondaryUpMoodPercent}
        students={secondaryStudents}
      />
      <DayOfWeekSlide dateRangeLabel={dateRangeLabel} educatorDayOfWeek={educatorDayOfWeek} mostUsedDay={mostUsedDay} />
      <GamesSlide dateRangeLabel={dateRangeLabel} topGames={topGames} activities={overview.activities} />
      <StoriesSlide dateRangeLabel={dateRangeLabel} topStories={topStories} stories={overview.stories} />
    </div>
  );
};

export default DistrictReportPDFSlides;
